import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import RefreshIcon from "@material-ui/icons/Refresh";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    // Phones < 480px must not horizontal-overflow. `min(480px, ...)` caps the
    // forced width to the viewport minus a 24px breathing margin. Combined
    // with the Dialog's `fullWidth` + `maxWidth="sm"` this gives a clean
    // 480px modal on desktop and a near-edge-to-edge card on phones.
    minWidth: "min(480px, calc(100vw - 24px))",
    padding: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    fontSize: 20,
    paddingBottom: 4,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: -4,
    paddingLeft: 24,
    paddingBottom: 12,
  },
  statRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  statLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 15,
    color: theme.palette.text.primary,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
  },
  totalBox: {
    background: theme.palette.type === "dark" ? "#1a2e22" : "#f0fdf4",
    borderRadius: 12,
    margin: "0 16px 8px",
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalLabel: { fontSize: 16, fontWeight: 600 },
  totalValue: { fontSize: 36, fontWeight: 800, color: "#059669" },
}));

/**
 * Conversation Starts breakdown — Raviv 2026-06-08.
 *
 * Opens when an agent's "התחלות שיחה" cell is clicked in the dashboard
 * attendants table. Shows the split between new-ticket starts and 24h+
 * reactivations. Read-only — no actions other than close.
 */
export default function ConversationStartsModal({ open, onClose, data }) {
  const classes = useStyles();
  if (!data) return null;

  const total = Number(data.total) || 0;
  const newTickets = Number(data.newTickets) || 0;
  const reactivations = Number(data.reactivations) || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Typography className={classes.title}>
          📊 התחלות שיחה — {data.name}
        </Typography>
      </DialogTitle>
      <Typography className={classes.subtitle}>
        מדד: הודעה ראשונה של הנציג בשיחה חדשה / שיחה שהתקררה 24+ שעות
      </Typography>
      <DialogContent style={{ padding: 0 }}>
        {/* סה"כ */}
        <Box className={classes.totalBox}>
          <Typography className={classes.totalLabel}>
            סה&quot;כ התחלות שיחה
          </Typography>
          <Typography className={classes.totalValue}>{total}</Typography>
        </Box>

        <Divider style={{ margin: "8px 16px" }} />

        {/* שיחות חדשות */}
        <Box className={classes.statRow}>
          <Box className={classes.statLabel}>
            <PlayCircleOutlineIcon style={{ color: "#059669" }} />
            <Box>
              <Typography style={{ fontWeight: 600 }}>שיחות חדשות</Typography>
              <Typography variant="caption" color="textSecondary">
                לקוח ממתין — נציג פתח ראשון
              </Typography>
            </Box>
          </Box>
          <Typography className={classes.statValue} style={{ color: "#059669" }}>
            {newTickets}
          </Typography>
        </Box>

        <Divider style={{ margin: "0 16px" }} />

        {/* שיחות מחודשות */}
        <Box className={classes.statRow}>
          <Box className={classes.statLabel}>
            <RefreshIcon style={{ color: "#d97706" }} />
            <Box>
              <Typography style={{ fontWeight: 600 }}>שיחות מחודשות</Typography>
              <Typography variant="caption" color="textSecondary">
                שיחה שעברו 24+ שעות — נציג פתח מחדש
              </Typography>
            </Box>
          </Box>
          <Typography className={classes.statValue} style={{ color: "#d97706" }}>
            {reactivations}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8 }}>
          סגור
        </Button>
      </DialogActions>
    </Dialog>
  );
}
